import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/context/AuthContext';
import { notificationsApi } from '@/lib/api';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import { 
  Truck, 
  Bell,
  ArrowLeft,
  Loader2,
  DollarSign,
  MessageSquare,
  CheckCircle,
  Package,
  HelpCircle,
  Smartphone,
  Mail,
  BellRing,
  AlertCircle
} from 'lucide-react';

const notificationSettings = [
  {
    key: 'quote_received',
    label: 'New Quotes',
    description: 'When a provider submits a quote on your job',
    icon: DollarSign,
    forRoles: ['customer']
  },
  {
    key: 'question_received',
    label: 'New Questions',
    description: 'When a provider asks a question about your job',
    icon: HelpCircle,
    forRoles: ['customer']
  },
  {
    key: 'question_answered',
    label: 'Question Answered',
    description: 'When a customer answers your question',
    icon: MessageSquare,
    forRoles: ['provider']
  },
  {
    key: 'quote_accepted',
    label: 'Quote Accepted',
    description: 'When your quote is accepted by a customer',
    icon: CheckCircle,
    forRoles: ['provider']
  },
  {
    key: 'job_accepted',
    label: 'Job Confirmed',
    description: 'When a job is confirmed and payment received',
    icon: CheckCircle,
    forRoles: ['customer', 'provider']
  },
  {
    key: 'job_picked_up',
    label: 'Job Picked Up',
    description: 'When your load has been picked up',
    icon: Truck,
    forRoles: ['customer']
  },
  {
    key: 'job_delivered',
    label: 'Job Delivered',
    description: 'When your load has been delivered',
    icon: Package,
    forRoles: ['customer', 'provider']
  },
  {
    key: 'payment_released',
    label: 'Payment Released',
    description: 'When payment is released after job completion',
    icon: DollarSign,
    forRoles: ['customer', 'provider']
  },
  {
    key: 'verification_update',
    label: 'Verification Updates',
    description: 'Updates about your account verification status',
    icon: CheckCircle,
    forRoles: ['provider']
  }
];

export default function NotificationSettingsPage() {
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preferences, setPreferences] = useState({});
  
  // Push notification hook
  const { 
    isSupported: pushSupported, 
    isSubscribed: pushSubscribed, 
    permission: pushPermission,
    loading: pushLoading,
    subscribe: subscribePush,
    unsubscribe: unsubscribePush
  } = usePushNotifications();

  useEffect(() => {
    fetchPreferences();
  }, []);

  const fetchPreferences = async () => {
    try {
      const response = await notificationsApi.getPreferences();
      setPreferences(response.data.preferences);
    } catch (error) {
      toast.error('Failed to load notification preferences');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (key, value) => {
    const newPrefs = { ...preferences, [key]: value };
    setPreferences(newPrefs);
    
    setSaving(true);
    try {
      await notificationsApi.updatePreferences(newPrefs);
      toast.success('Preferences saved');
    } catch (error) {
      toast.error('Failed to save preferences');
      setPreferences(preferences); // Revert
    } finally {
      setSaving(false);
    }
  };

  const handleEnableAll = async () => {
    const allEnabled = {};
    notificationSettings.forEach(s => {
      if (s.forRoles.includes(user?.role)) {
        allEnabled[s.key] = true;
      }
    });
    setPreferences(allEnabled);
    
    setSaving(true);
    try {
      await notificationsApi.updatePreferences(allEnabled);
      toast.success('All notifications enabled');
    } catch (error) {
      toast.error('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  const handlePushToggle = async () => {
    if (pushLoading) return;
    
    try {
      if (pushSubscribed) {
        await unsubscribePush(token);
        toast.success('Push notifications disabled');
      } else {
        await subscribePush(token);
        toast.success('Push notifications enabled');
      }
    } catch (error) {
      if (error.message === 'Notification permission denied') {
        toast.error('Please allow notifications in your browser settings');
      } else {
        toast.error('Failed to update push notifications');
      }
      console.error('Push toggle error:', error);
    }
  };

  const relevantSettings = notificationSettings.filter(s => 
    s.forRoles.includes(user?.role)
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link 
              to={user?.role === 'customer' ? '/dashboard' : '/provider'} 
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="hidden sm:inline">Back</span>
            </Link>
            <div className="h-6 w-px bg-border" />
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-primary rounded-sm flex items-center justify-center">
                <Truck className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-sm" style={{ fontFamily: 'Barlow Condensed' }}>
                THM
              </span>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
                NOTIFICATION SETTINGS
              </h1>
              <p className="text-muted-foreground">
                Choose how and when you want to be notified
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={handleEnableAll}>
              Enable All
            </Button>
          </div>

          {/* Notification Channels Section */}
          <Card className="mb-6 border-border rounded-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg" style={{ fontFamily: 'Barlow Condensed' }}>
                NOTIFICATION CHANNELS
              </CardTitle>
              <CardDescription>
                Choose how you want to receive notifications
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Push Notifications Toggle */}
              <div className="flex items-center justify-between p-4 bg-muted/50 rounded-sm">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-primary/10 rounded-sm flex items-center justify-center">
                    <BellRing className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <Label className="text-base font-medium">Push Notifications</Label>
                    <p className="text-sm text-muted-foreground">
                      {pushSupported 
                        ? 'Get instant browser notifications' 
                        : 'Not supported in this browser'}
                    </p>
                    {pushPermission === 'denied' && (
                      <p className="text-xs text-destructive flex items-center gap-1 mt-1">
                        <AlertCircle className="w-3 h-3" />
                        Blocked in browser - enable in settings
                      </p>
                    )}
                  </div>
                </div>
                <Switch
                  checked={pushSubscribed}
                  onCheckedChange={handlePushToggle}
                  disabled={!pushSupported || pushLoading || pushPermission === 'denied'}
                  data-testid="toggle-push-notifications"
                />
              </div>

              {/* Email Notifications (info only) */}
              <div className="flex items-center justify-between p-4 bg-muted/50 rounded-sm">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-emerald-500/10 rounded-sm flex items-center justify-center">
                    <Mail className="w-5 h-5 text-emerald-500" />
                  </div>
                  <div>
                    <Label className="text-base font-medium">Email Notifications</Label>
                    <p className="text-sm text-muted-foreground">
                      Receive updates to your registered email
                    </p>
                    <Badge variant="outline" className="mt-1 text-xs bg-emerald-500/10 text-emerald-600 border-emerald-500/20">
                      Active via Resend
                    </Badge>
                  </div>
                </div>
                <Switch checked={true} disabled data-testid="toggle-email-notifications" />
              </div>

              {/* SMS Notifications (info only) */}
              <div className="flex items-center justify-between p-4 bg-muted/50 rounded-sm opacity-70">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-muted rounded-sm flex items-center justify-center">
                    <Smartphone className="w-5 h-5 text-muted-foreground" />
                  </div>
                  <div>
                    <Label className="text-base font-medium">SMS Notifications</Label>
                    <p className="text-sm text-muted-foreground">
                      Get text messages for urgent updates
                    </p>
                    <Badge variant="outline" className="mt-1 text-xs">
                      Active (Mock)
                    </Badge>
                  </div>
                </div>
                <Switch checked={true} disabled data-testid="toggle-sms-notifications" />
              </div>
            </CardContent>
          </Card>

          {/* Notification Types Section */}
          <Card className="border-border rounded-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg" style={{ fontFamily: 'Barlow Condensed' }}>
                NOTIFICATION TYPES
              </CardTitle>
              <CardDescription>
                Choose which events trigger notifications
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y divide-border">
                {relevantSettings.map((setting) => {
                  const Icon = setting.icon;
                  const isEnabled = preferences[setting.key] !== false;
                  
                  return (
                    <div 
                      key={setting.key}
                      className="p-4 flex items-center justify-between gap-4"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-muted rounded-sm flex items-center justify-center">
                          <Icon className="w-5 h-5 text-muted-foreground" />
                        </div>
                        <div>
                          <Label className="text-base font-medium">{setting.label}</Label>
                          <p className="text-sm text-muted-foreground">{setting.description}</p>
                        </div>
                      </div>
                      <Switch
                        checked={isEnabled}
                        onCheckedChange={(value) => handleToggle(setting.key, value)}
                        disabled={saving}
                        data-testid={`toggle-${setting.key}`}
                      />
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          <p className="text-sm text-muted-foreground text-center mt-6">
            Email and SMS notifications are currently in mock mode. Push notifications are live.
          </p>
        </motion.div>
      </main>
    </div>
  );
}
